import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/lib/services-data";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";
import { SERVICE_SEO, CITIES, INDUSTRIES } from "@/data/programmatic-seo";

const SERVICES_FAQ = [
  {
    q: "Who actually does the work at Spin Labs — seniors, or juniors?",
    a: "Every engagement is led by a senior engineer who has shipped multiple production systems in the category. No pyramids, no bait-and-switch — the person on your discovery call is the person writing the code.",
  },
  {
    q: "How fast can you go from brief to production?",
    a: "Typical software / SaaS builds are 6–12 weeks. Mobile apps with store submission: 8–14 weeks. Custom AI / RAG systems: 4–10 weeks depending on data prep. We publish a brief-to-estimate turnaround of under 48 hours.",
  },
  {
    q: "Do you work with startups or enterprises?",
    a: "Both. We work with seed-stage founders shipping their first production release and with Series B+ and enterprise teams replatforming legacy systems. The engagement model flexes — the engineering bar doesn't.",
  },
  {
    q: "How do you handle compliance (HIPAA, SOC2, RBI, GDPR)?",
    a: "We build with compliance-aware defaults from day one — encryption posture, audit trails, data residency, minimal-PII architecture — and sign BAAs, DPAs, and standard NDAs as part of kickoff rather than retrofitting them.",
  },
  {
    q: "What stacks do you work with?",
    a: "Mobile: React Native, Swift, Kotlin. Backend: Python (FastAPI), Node, Go. Frontend: React, Next.js, TypeScript. Cloud: AWS, GCP, Vercel, Cloudflare. AI: OpenAI, Anthropic, Gemini, LangChain, Pinecone, Weaviate. We pick stack per project, not by default.",
  },
  {
    q: "Where is Spin Labs based and who do you work with geographically?",
    a: "Headquartered in Delhi, India. We work with teams across Indian metros (Delhi, Gurugram, Noida, Bangalore, Mumbai, Hyderabad, Pune, Chennai) and globally (San Francisco, London, Singapore, Dubai). Working hours overlap is set per-engagement.",
  },
  {
    q: "How does pricing work?",
    a: "Fixed-scope statements of work for clearly scoped deliverables; monthly retainers for ongoing engineering / SEO / marketing. Indian engagements typically range ₹6L–₹40L, global engagements start at USD equivalents. Brief-to-estimate in under 48 hours — no vanity discovery calls.",
  },
];

const SERVICES_JSONLD = (services) => [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Services — Spin Labs",
    url: "https://spinlabs.in/services",
    description:
      "Mobile, AI, SaaS, software, marketing, SEO and growth engineering services delivered by Spin Labs.",
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: services.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Service",
        name: s.title,
        description: s.desc,
        provider: { "@type": "Organization", name: "Spin Labs", url: "https://spinlabs.in" },
        areaServed: "Worldwide",
        url: "https://spinlabs.in/services",
      },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SERVICES_FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
  ]),
];

const STACK = {
  Mobile: ["React Native", "Swift", "Kotlin", "Expo", "Firebase"],
  AI: ["OpenAI", "Anthropic", "Gemini", "LangChain", "Pinecone", "Weaviate"],
  Backend: ["Python", "FastAPI", "Node.js", "Go", "PostgreSQL", "MongoDB"],
  Frontend: ["React", "Next.js", "TypeScript", "Tailwind", "Framer Motion"],
  Cloud: ["AWS", "Vercel", "GCP", "Cloudflare", "Docker", "Kubernetes"],
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Services — Mobile, AI, SaaS, Software, Marketing, SEO"
        path="/services"
        description="Spin Labs ships mobile apps, AI-native websites, custom AI systems, SaaS platforms, software, SEO and marketing for ambitious teams."
        keywords="mobile app development, AI agency, SaaS development, custom AI systems, SEO services, marketing automation, software studio"
        jsonLd={SERVICES_JSONLD(SERVICES)}
      />
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="spotlight w-[500px] h-[500px] bg-[#00F0FF]/15 top-0 left-1/4" />
        <div className="spotlight w-[500px] h-[500px] bg-[#FF00FF]/15 top-10 right-1/4" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="tag-chip mb-6">Services</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-balance max-w-4xl">
              Every layer of your <span className="gradient-text">product</span>. Handled.
            </h1>
            <p className="mt-6 max-w-2xl text-white/60 leading-relaxed">
              Seven practices. One delivery engine. We own the strategy, the craft, and the ship date.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-detail-${s.id}`}
                className={`grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch`}
              >
                <div className={`lg:col-span-3 ${reverse ? "lg:order-2" : ""} card-hover rounded-2xl p-8 lg:p-10`}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center border"
                         style={{ borderColor: `${s.color}50`, background: `${s.color}10` }}>
                      <Icon className="w-7 h-7" style={{ color: s.color }} />
                    </div>
                    <div>
                      <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em]" style={{ color: s.color }}>{s.tag}</div>
                      <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter">{s.title}</h2>
                    </div>
                  </div>
                  <p className="text-white/70 leading-relaxed mb-6 max-w-2xl">{s.desc}</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-sm text-white/80">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: s.color }} />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="mt-8 btn-secondary group">
                    Discuss your project
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </Link>
                </div>

                <div className={`lg:col-span-2 ${reverse ? "lg:order-1" : ""} relative rounded-2xl overflow-hidden border border-white/10 min-h-[280px]`}>
                  <div className="absolute inset-0 grid-bg-lg opacity-40" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative">
                      <div className="w-48 h-48 rounded-full blur-3xl opacity-40" style={{ background: s.color }} />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Icon className="w-24 h-24" style={{ color: s.color, filter: `drop-shadow(0 0 20px ${s.color})` }} />
                      </div>
                    </div>
                  </div>
                  <div className="scanline" />
                  <div className="absolute bottom-4 left-4 right-4 glass rounded-lg px-3 py-2">
                    <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-white/60">
                      {String(i + 1).padStart(2, "0")} / {SERVICES.length.toString().padStart(2, "0")} · {s.title}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Programmatic index — links Googlebot to all 140 city × service and
          industry × service pages. Also useful for real humans browsing. */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">By city</div>
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter mb-3">
            Shipping for teams in <span className="gradient-text">your city</span>.
          </h2>
          <p className="text-white/60 mb-10 max-w-2xl">
            We work with clients across Indian metros and globally. Each city page covers timezone overlap,
            on-site availability, and how we collaborate with your team locally.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.values(SERVICE_SEO).flatMap((svc) =>
              CITIES.map((city) => ({
                key: `${svc.slug}-in-${city.slug}`,
                label: `${svc.title} in ${city.name}`,
                to: `/services/${svc.slug}-in-${city.slug}`,
              }))
            ).slice(0, 36).map((r) => (
              <Link key={r.key} to={r.to}
                    className="group p-3 rounded-lg border border-white/5 hover:border-[#00F0FF]/40 hover:bg-white/5 transition-all text-sm text-white/70 hover:text-white">
                {r.label}
                <ArrowUpRight className="inline-block w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>

          <div className="tag-chip mb-4 mt-16">By industry</div>
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter mb-3">
            Specialised for <span className="neon-text-magenta">your industry</span>.
          </h2>
          <p className="text-white/60 mb-10 max-w-2xl">
            Compliance posture, regulatory awareness, and representative outcomes per industry —
            not the same generic deck for every vertical.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.values(SERVICE_SEO).flatMap((svc) =>
              INDUSTRIES.map((ind) => ({
                key: `${svc.slug}-for-${ind.slug}`,
                label: `${svc.title} for ${ind.display}`,
                to: `/services/${svc.slug}-for-${ind.slug}`,
              }))
            ).slice(0, 24).map((r) => (
              <Link key={r.key} to={r.to}
                    className="group p-3 rounded-lg border border-white/5 hover:border-[#FF00FF]/40 hover:bg-white/5 transition-all text-sm text-white/70 hover:text-white">
                {r.label}
                <ArrowUpRight className="inline-block w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — paired with FAQPage schema in SERVICES_JSONLD for SERP rich results */}
      <section className="relative py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">FAQ</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mb-10">
            Questions we get <span className="gradient-text">every week</span>.
          </h2>
          <div className="space-y-3">
            {SERVICES_FAQ.map((f) => (
              <details key={f.q} className="group card-hover rounded-xl p-6">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-display text-lg font-bold text-white">{f.q}</span>
                  <span className="font-mono-neo text-xs text-[#00F0FF] mt-1 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-4 text-white/70 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Tech stack */}
      <section className="relative py-24 bg-[#07070A] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="tag-chip mb-4">Tech Stack</div>
          <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter max-w-3xl">
            Tools we love. Battle-tested. <span className="neon-text-magenta">No fluff.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {Object.entries(STACK).map(([k, arr]) => (
              <div key={k} className="p-6 border border-white/10 rounded-xl hover:border-[#00F0FF]/40 transition-colors">
                <div className="font-mono-neo text-xs uppercase tracking-[0.2em] text-[#00F0FF] mb-4">{k}</div>
                <ul className="space-y-2 text-sm text-white/80">
                  {arr.map((t) => <li key={t} className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-[#00F0FF]" />{t}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
